import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignImpactMarker,
  InsightsDailyRevenuePoint,
} from "@shared-core/data/api-types";
import { CampaignImpactChart } from "./CampaignImpactChart";

// The Campaign Impact tab: loads the composed /views/insights/campaign-impact
// series and renders the booked-revenue trend with campaign send markers.
// Covers loading/empty/error, mirroring the Daily-tab template.

type State =
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "ready";
      days: InsightsDailyRevenuePoint[];
      campaignMarkers: CampaignImpactMarker[];
    };

export function CampaignImpactTab() {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const result = await adapter.getInsightsCampaignImpact();
      setState({
        status: "ready",
        days: result.days,
        campaignMarkers: result.campaign_markers,
      });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading campaign impact…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load campaign impact. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <CampaignImpactChart
      days={state.days}
      campaignMarkers={state.campaignMarkers}
    />
  );
}
